import { router } from '@forge/bridge'
import { useSnapshot } from 'valtio'
import { Logo } from '@/components/Logo/Logo'
import { SafeHtml } from '@/components/SafeHtml/SafeHtml'
import { state, useRadarNav } from '@/lib/state'
import styles from './Footer.module.css'

export function Footer() {
  const { context, radarConfig, embedConfig } = useSnapshot(state)
  const labels = radarConfig?.labels || {}
  const { rootRoute } = useRadarNav()
  const footerLabel = labels.footer

  // Generate the Compass admin link from the localId
  const adminLink = context?.localId
    ? context.localId === 'local-dev'
      ? '/admin'
      : `${context.siteUrl}/compass/apps/${encodeURIComponent(`b:${btoa(context.localId.replace('techradarGlobal', 'techradarAdmin'))}`)}`
    : undefined

  const handleAdminClick = (e: React.MouseEvent) => {
    e.preventDefault()
    console.info('Footer', 'Admin link clicked', {
      localId: context?.localId,
      adminLink,
      hasRouter: !!router,
      hasRouterOpen: !!(router && typeof router.open === 'function')
    })

    if (context?.localId === 'local-dev') {
      // For development, use React Router navigation
      console.info('Footer', 'Using React Router navigation (dev mode)')
      rootRoute('admin/items')
    } else {
      // For Forge platform (staging/production), use router.open to open in new tab
      if (router && typeof router.open === 'function' && adminLink) {
        console.info('Footer', 'Using router.open (Forge platform)', {
          adminLink
        })
        router.open(adminLink)
      } else {
        console.info('Footer', 'router.open not available', {
          hasRouter: !!router,
          hasRouterOpen: !!(router && typeof router.open === 'function'),
          adminLink
        })
      }
    }
  }

  return (
    <div className={styles.footer} data-testid="footer">
      <div className={styles.branding} data-testid="footer-branding">
        <Logo />

        <div className={styles.description} data-testid="footer-description">
          <div className="flex items-center space-x-2">
            {embedConfig ? null : (
              <a
                href={adminLink}
                className="inline-flex items-center hover:text-blue-500 transition-colors pr-2"
                data-testid="footer-admin-link"
                onClick={handleAdminClick}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="size-3"
                >
                  <title>Configure Tech Radar</title>
                  <path
                    fillRule="evenodd"
                    d="M7.84 1.804A1 1 0 0 1 8.82 1h2.36a1 1 0 0 1 .98.804l.331 1.652a6.993 6.993 0 0 1 1.929 1.115l1.598-.54a1 1 0 0 1 1.186.447l1.18 2.044a1 1 0 0 1-.205 1.251l-1.267 1.113a7.047 7.047 0 0 1 0 2.228l1.267 1.113a1 1 0 0 1 .206 1.25l-1.18 2.045a1 1 0 0 1-1.187.447l-1.598-.54a6.993 6.993 0 0 1-1.929 1.115l-.33 1.652a1 1 0 0 1-.98.804H8.82a1 1 0 0 1-.98-.804l-.331-1.652a6.993 6.993 0 0 1-1.929-1.115l-1.598.54a1 1 0 0 1-1.186-.447l-1.18-2.044a1 1 0 0 1 .205-1.251l1.267-1.114a7.05 7.05 0 0 1 0-2.227L1.821 7.773a1 1 0 0 1-.206-1.25l1.18-2.045a1 1 0 0 1 1.187-.447l1.598.54A6.992 6.992 0 0 1 7.51 3.456l.33-1.652ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            )}
            <div className="flex-1 flex items-center">
              <SafeHtml html={footerLabel} className="m-0 inline" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
